import React, { useState } from 'react';
import { Icon } from './ui/Icon.jsx';
import { useQuery } from '@tanstack/react-query';
import { supaFetch } from '../lib/supabase.js';
import { fmt } from '../lib/utils.js';
import T from '../locales/translations.js';

// ─── Data hooks ───────────────────────────────────────────────────────────────
const useTopSpenders  = (days) => useQuery({
  queryKey: ['analytics', 'top_spenders', days],
  queryFn: () => supaFetch('POST', 'rpc/analytics_top_spenders', { p_days: days }, '', { 'Prefer': 'return=representation' }).then(r => r.data || []),
  staleTime: 5 * 60_000,
});
const useChurnRisk    = (days) => useQuery({
  queryKey: ['analytics', 'churn_risk', days],
  queryFn: () => supaFetch('POST', 'rpc/analytics_churn_risk', { p_days: days }, '', { 'Prefer': 'return=representation' }).then(r => r.data || []),
  staleTime: 5 * 60_000,
});
const useMarketBasket = (days) => useQuery({
  queryKey: ['analytics', 'market_basket', days],
  queryFn: () => supaFetch('POST', 'rpc/analytics_market_basket', { p_days: days }, '', { 'Prefer': 'return=representation' }).then(r => r.data || []),
  staleTime: 10 * 60_000,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const Card = ({ title, subtitle, children, loading, error }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: 22, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', marginBottom: 24 }}>
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: 'var(--sedra-forest)' }}>{title}</h3>
      {subtitle && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#aaa' }}>{subtitle}</p>}
    </div>
    {loading ? (
      <div style={{ color: '#aaa', padding: '20px 0', textAlign: 'center' }}>Loading…</div>
    ) : error ? (
      <div style={{ color: 'var(--status-danger)', fontSize: 13 }}>Error: {error}</div>
    ) : children}
  </div>
);

const Th = ({ children, right }) => (
  <th style={{ padding: '8px 12px', textAlign: right ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: 'var(--sedra-sage-ink)', background: '#f7f5f0', whiteSpace: 'nowrap' }}>
    {children}
  </th>
);
const Td = ({ children, right, mono }) => (
  <td style={{ padding: '9px 12px', textAlign: right ? 'right' : 'left', fontSize: 13, fontFamily: mono ? 'monospace' : 'inherit', color: 'var(--sedra-forest)', borderTop: '1px solid #f0ede8' }}>
    {children}
  </td>
);

// ─── Main component ───────────────────────────────────────────────────────────
export default function Analytics({ lang }) {
  const ar = lang === 'ar';
  const [spenderDays, setSpenderDays]   = useState(30);
  const [churnDays,   setChurnDays]     = useState(60);
  const [basketDays,  setBasketDays]    = useState(90);

  const { data: spenders  = [], isLoading: sl, error: se } = useTopSpenders(spenderDays);
  const { data: churners  = [], isLoading: cl, error: ce } = useChurnRisk(churnDays);
  const { data: basket    = [], isLoading: bl, error: be } = useMarketBasket(basketDays);

  const DayPicker = ({ value, onChange }) => (
    <select value={value} onChange={e => onChange(Number(e.target.value))}
      style={{ padding: '4px 10px', borderRadius: 6, border: '1.5px solid var(--sedra-line)', fontSize: 12, background: '#fff', color: 'var(--sedra-forest-soft)', cursor: 'pointer' }}>
      {[[7,'7d'],[30,'30d'],[60,'60d'],[90,'90d'],[180,'180d']].map(([v,l]) => (
        <option key={v} value={v}>{l}</option>
      ))}
    </select>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 0 40px' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: 'var(--sedra-forest)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="ChartColumn" size={20} /> {ar ? 'لوحة التحليلات' : 'Analytics'}
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#aaa' }}>
          {ar ? 'رؤى تجارية لدعم فريق المبيعات' : 'Business intelligence for your sales team'}
        </p>
      </div>

      {/* ── Top Spenders ── */}
      <Card
        title={<><Icon name="Trophy" size={15} /> {ar ? 'أعلى المشترين إنفاقاً' : 'Top Spenders'}</>}
        subtitle={ar ? `آخر ${spenderDays} يوم` : `Last ${spenderDays} days`}
        loading={sl} error={se?.message}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <DayPicker value={spenderDays} onChange={setSpenderDays} />
        </div>
        {spenders.length === 0 ? (
          <div style={{ color: '#bbb', textAlign: 'center', padding: 24 }}>{ar ? 'لا بيانات' : 'No data yet'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <Th>#</Th>
                <Th>{ar ? 'الشركة' : 'Company'}</Th>
                <Th>{ar ? 'المسؤول' : 'Contact'}</Th>
                <Th right>{ar ? 'عدد الطلبات' : 'Orders'}</Th>
                <Th right>{ar ? 'الإجمالي' : 'Total Spent'}</Th>
                <Th>{ar ? 'آخر طلب' : 'Last Order'}</Th>
              </tr></thead>
              <tbody>
                {spenders.map((r, i) => (
                  <tr key={i}>
                    <Td><span style={{ fontWeight: 800, color: i === 0 ? 'var(--status-warn)' : i === 1 ? '#6b7280' : i === 2 ? '#b45309' : '#aaa', display: 'inline-flex' }}>{i < 3 ? <Icon name="Award" size={15} /> : i + 1}</span></Td>
                    <Td><span style={{ fontWeight: 700 }}>{r.company_name || '—'}</span></Td>
                    <Td><div style={{ fontSize: 12, color: '#555' }}>{r.contact_name}</div><div style={{ fontSize: 11, color: '#aaa' }}>{r.email}</div></Td>
                    <Td right><span style={{ fontWeight: 700 }}>{r.order_count}</span></Td>
                    <Td right><span style={{ fontWeight: 800, color: 'var(--status-success)' }}>{fmt(r.total_spent)}</span></Td>
                    <Td><span style={{ fontSize: 12, color: '#888' }}>{r.last_order_at ? new Date(r.last_order_at).toLocaleDateString(ar ? 'ar-EG' : 'en-GB', { month: 'short', day: 'numeric' }) : '—'}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Churn Risk ── */}
      <Card
        title={<><Icon name="TriangleAlert" size={15} /> {ar ? 'عملاء معرّضون للفقدان' : 'Churn Risk'}</>}
        subtitle={ar ? `لم يطلبوا منذ ${churnDays}+ يوم` : `No order in ${churnDays}+ days`}
        loading={cl} error={ce?.message}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <DayPicker value={churnDays} onChange={setChurnDays} />
        </div>
        {churners.length === 0 ? (
          <div style={{ color: '#bbb', textAlign: 'center', padding: 24 }}>{ar ? 'لا يوجد عملاء معرّضون للفقدان' : 'No churn risk — great!'}</div>
        ) : (
          <>
            <div style={{ background: 'var(--status-warn-bg)', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 14px', marginBottom: 12, fontSize: 12, color: 'var(--status-warn-dark)' }}>
              {ar ? `${churners.length} عميل لم يطلب منذ ${churnDays}+ يوم — اتصل بهم!` : `${churners.length} buyer${churners.length > 1 ? 's' : ''} haven't ordered in ${churnDays}+ days — reach out!`}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <Th>{ar ? 'الشركة' : 'Company'}</Th>
                  <Th>{ar ? 'الهاتف' : 'Phone'}</Th>
                  <Th right>{ar ? 'أيام الغياب' : 'Days Silent'}</Th>
                  <Th right>{ar ? 'طلبات سابقة' : 'Past Orders'}</Th>
                  <Th>{ar ? 'آخر طلب' : 'Last Order'}</Th>
                </tr></thead>
                <tbody>
                  {churners.map((r, i) => (
                    <tr key={i}>
                      <Td><div style={{ fontWeight: 700 }}>{r.company_name || '—'}</div><div style={{ fontSize: 11, color: '#aaa' }}>{r.contact_name}</div></Td>
                      <Td mono>{r.phone || '—'}</Td>
                      <Td right>
                        <span style={{ fontWeight: 800, color: r.days_since > 90 ? 'var(--status-danger)' : 'var(--status-warn)', fontSize: 15 }}>{r.days_since}</span>
                        <span style={{ fontSize: 10, color: '#aaa', marginInlineStart: 4 }}>{ar ? 'يوم' : 'd'}</span>
                      </Td>
                      <Td right>{r.total_orders}</Td>
                      <Td><span style={{ fontSize: 12, color: '#888' }}>{r.last_order_at ? new Date(r.last_order_at).toLocaleDateString(ar ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</span></Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* ── Market Basket ── */}
      <Card
        title={<><Icon name="ShoppingCart" size={15} /> {ar ? 'المنتجات المشتراة معاً' : 'Frequently Bought Together'}</>}
        subtitle={ar ? `أزواج المنتجات في نفس الطلب — آخر ${basketDays} يوم` : `SKU pairs co-occurring in same order — last ${basketDays} days`}
        loading={bl} error={be?.message}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <DayPicker value={basketDays} onChange={setBasketDays} />
        </div>
        {basket.length === 0 ? (
          <div style={{ color: '#bbb', textAlign: 'center', padding: 24 }}>{ar ? 'لا بيانات كافية بعد' : 'Not enough data yet — needs ≥2 orders with shared SKUs'}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <Th>#</Th>
                <Th>SKU A</Th>
                <Th>SKU B</Th>
                <Th right>{ar ? 'معاً في' : 'Co-orders'}</Th>
              </tr></thead>
              <tbody>
                {basket.map((r, i) => (
                  <tr key={i}>
                    <Td><span style={{ color: '#aaa', fontSize: 12 }}>{i + 1}</span></Td>
                    <Td mono><span style={{ background: '#f0f9f0', color: 'var(--sedra-forest)', padding: '2px 8px', borderRadius: 5, fontWeight: 600 }}>{r.sku_a}</span></Td>
                    <Td mono><span style={{ background: '#f0f9f0', color: 'var(--sedra-forest)', padding: '2px 8px', borderRadius: 5, fontWeight: 600 }}>{r.sku_b}</span></Td>
                    <Td right><span style={{ fontWeight: 800, fontSize: 15, color: 'var(--sedra-forest-soft)' }}>{r.co_occurrence}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
